import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { useAuth } from '../../context/AuthContext';
import { GHL_STANDARD_FIELDS, STRUCTURAL_TYPES } from '../../lib/formConstants';
import axios from 'axios';
import CodeTextarea, { formatJinjaJson } from './CodeTextarea';
import { maskKey } from '../../lib/mask';
import WebhookDestinationStatus from '../webhooks/WebhookDestinationStatus';
import { isStoredCredential, STORED_CREDENTIAL } from '../../lib/forwarderCredentials';
import { isOrgAdminUser } from '../../utils/orgRoles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STANDARD_VAR_HINTS = ['{{ form.name }}', '{{ utm.source }}', '{{ utm.medium }}', '{{ submission.id }}'];
const MAX_TAGS = 25;
const MAX_TAG_LEN = 64;

/**
 * A write-only credential input. A saved credential arrives as
 * STORED_CREDENTIAL: the box shows empty with a "Saved" placeholder, typing
 * replaces it, and clearing what was typed goes back to "keep the saved one".
 */
function SecretInput({ value, onChange, placeholder, className, disabled, testId }) {
    const [wasStored] = useState(isStoredCredential(value));
    const stored = isStoredCredential(value);
    return (
        <input
            type="password"
            data-testid={testId}
            value={stored ? '' : (value || '')}
            onChange={e => onChange(e.target.value === '' && wasStored ? STORED_CREDENTIAL : e.target.value)}
            placeholder={stored ? 'Saved — leave blank to keep' : placeholder}
            autoComplete="new-password"
            disabled={disabled}
            className={className}
        />
    );
}

/** Human-readable auth label for the generic API connection summary. */
function authLabel(type) {
    return { bearer: 'Bearer', api_key: 'API key', basic: 'Basic', custom_headers: 'Custom headers' }[type] || type;
}

/** A collapsible settings section rendered as a bordered card with a summarising
 *  header. open/closed state is local component state and is never written into
 *  config. When forceOpen flips true (stale mapping / GHL error) the section
 *  re-opens itself, but the user can always collapse it again afterwards — the
 *  problem stays visible in the header summary, so a red state is never hidden. */
function Section({ title, summary, defaultOpen = false, forceOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    const prevForced = useRef(forceOpen);
    useEffect(() => {
        if (forceOpen && !prevForced.current) setOpen(true);
        prevForced.current = forceOpen;
    }, [forceOpen]);
    const isOpen = open;
    return (
        <Collapsible open={isOpen} onOpenChange={v => setOpen(v)} className="rounded-lg border border-stone-200 bg-white">
            <CollapsibleTrigger className="w-full flex items-center justify-between gap-3 py-3 px-4 text-left rounded-lg hover:bg-stone-50 transition-colors min-w-0">
                <span className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0 text-stone-500" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 text-stone-400" />}
                    <span className="text-sm font-semibold text-stone-800 truncate">{title}</span>
                </span>
                <span className="text-xs text-stone-400 text-right truncate min-w-0">{summary}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className={isOpen ? 'px-4 pb-4 pt-4 space-y-4 border-t border-stone-100' : ''}>
                {children}
            </CollapsibleContent>
        </Collapsible>
    );
}

export default function ForwarderEditor({ open, onClose, onSave, forwarder, formFields, allForwarders = [], filterStepId = null, formId = null }) {
    const [config, setConfig] = useState({ name: '', type: 'gohighlevel', method: 'POST', url: '', auth: { type: 'none' }, payload_template: '', enabled: true, ...forwarder });
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [ghlFields, setGhlFields] = useState(null);   // null = not loaded
    const [ghlError, setGhlError] = useState(null);
    const [loadingFields, setLoadingFields] = useState(false);
    const [preview, setPreview] = useState(null);
    const [tagInput, setTagInput] = useState('');
    const [tagHint, setTagHint] = useState(null);
    const textareaRef = useRef(null);
    const { getAuthHeader, user } = useAuth();
    // Where a forwarder delivers (type, URL, auth, GHL location/token) and the
    // admin-only actions (Verify, Reveal, Test, Preview, Load GHL fields) are an
    // org-admin decision; the server refuses anyone else (PR #740 F6/N2). Other
    // edits — name, payload, mappings, tags — stay open.
    const canEditDestination = isOrgAdminUser(user);

    // Build variable hints from actual form fields + standard vars
    const varHints = useMemo(() => {
        const fields = formFields || [];
        const fieldVars = fields
            .filter(f => f.field_id || f.id)
            .map(f => `{{ answers.${f.field_id || f.id} }}`);
        // Deduplicate and combine: field vars first, then standard vars
        return [...new Set([...fieldVars, ...STANDARD_VAR_HINTS])];
    }, [formFields]);

    // Available correlation sources: GHL forwarders from OTHER step nodes
    // that don't already have a correlation_source_step_id (i.e., they're producers)
    const correlationSources = useMemo(() => {
        const currentStepId = config.step_id || filterStepId;
        return (allForwarders || []).filter(f =>
            f.step_id !== currentStepId &&          // exclude self
            f.type === 'gohighlevel' &&              // only GHL forwarders
            !f.correlation_source_step_id &&         // only producers (no source set)
            f.step_id                                // must have a step_id
        );
    }, [allForwarders, config.step_id, filterStepId]);

    // Reset config + clear stale GHL field state when switching forwarders or reopening the dialog.
    // Previously, ghlFields was NOT cleared here — switching between forwarders kept the previous
    // forwarder's custom field list, and the mapping dropdowns showed stale or no options.
    useEffect(() => {
        setConfig({ name: '', type: 'gohighlevel', method: 'POST', url: '', auth: { type: 'none' }, payload_template: '', enabled: true, ...forwarder });
        setTestResult(null);
        setGhlFields(null);
        setGhlError(null);
        setTagInput('');
        setTagHint(null);
    }, [forwarder, open]);

    // ── Auto-load GHL custom fields on mount ──
    // Previously, GHL custom fields were only fetched when the user manually clicked
    // "Load GHL fields". On initial mount, ghlFields was null, so the custom-field
    // <optgroup> rendered no <option> elements. The browser fell back to the first
    // <option value="">Not mapped</option>, making existing mappings appear empty
    // even though config.ghl_field_mappings data was intact in the form config.
    // This effect auto-loads fields when the dialog opens with credentials present.
    useEffect(() => {
        if (!open) return;
        if (config.type !== 'gohighlevel') return;
        const locId = config.ghl_location_id ?? config.location_id ?? '';
        const apiKey = config.ghl_api_key ?? config.api_key ?? '';
        if (!locId || !apiKey) return;  // no credentials yet — user must enter them manually
        if (!canEditDestination) return;  // the custom-fields endpoint is admin-only
        if (ghlFields !== null) return;  // already loaded (or attempted) — don't refetch
        // Fire-and-forget — loadGhlFields handles its own loading/error state
        loadGhlFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, config.type, config.ghl_location_id, config.location_id, config.ghl_api_key, config.api_key, canEditDestination]);

    const update = (key, val) => setConfig(c => ({ ...c, [key]: val }));

    // CASA 7.1.2 / 7.3.2 — verify-now and the one-time signing-secret reveal act on
    // the SAVED destination, so they need the form id (the URL identifies it).
    const verifyDestination = formId && canEditDestination
        ? async () => (await axios.post(`${API}/forms/${formId}/forwarders/verify`, { url: config.url }, { headers: getAuthHeader() })).data
        : null;
    const revealSecret = formId && canEditDestination
        ? async (rotate) => (await axios.post(`${API}/forms/${formId}/forwarders/signing-secret`, { url: config.url, rotate }, { headers: getAuthHeader() })).data.signing_secret
        : null;
    const updateAuth = (key, val) => setConfig(c => ({ ...c, auth: { ...c.auth, [key]: val } }));

    const test = async () => {
        setTesting(true); setTestResult(null);
        try {
            // form_id lets the server sign the test with the saved destination's secret.
            const res = await axios.post(`${API}/forms/forwarders/test`, { config, sample_data: null, form_id: formId }, { headers: getAuthHeader() });
            if (res.data.success) {
                setTestResult({ success: true, message: res.data.message || `200 OK (${res.data.latency_ms ?? 0}ms)` });
            } else {
                setTestResult({ success: false, message: res.data.error || 'Delivery failed' });
            }
        } catch (err) { setTestResult({ success: false, message: err.response?.data?.detail || 'Connection failed' }); }
        finally { setTesting(false); }
    };

    const loadGhlFields = async () => {
        setLoadingFields(true); setGhlError(null);
        try {
            // A saved token arrives masked; form_id + the forwarder's identity let
            // the server use the stored one.
            const res = await axios.post(`${API}/forms/forwarders/ghl/custom-fields`, {
                ghl_location_id: config.ghl_location_id ?? config.location_id ?? '',
                ghl_api_key: config.ghl_api_key ?? config.api_key ?? '',
                form_id: formId,
                step_id: config.step_id,
                credentials_ref: config.credentials_ref,
                name: config.name,
            }, { headers: getAuthHeader() });
            if (res.data.valid) setGhlFields(res.data.fields);
            else setGhlError(res.data.error || 'Could not load GHL fields');
        } catch (err) {
            setGhlError(err.response?.data?.detail || 'Could not load GHL fields');
        } finally { setLoadingFields(false); }
    };

    // ── Tag helpers (add-only; chips edit only this forwarder's list) ──
    const cleanTag = (raw) => {
    let t = String(raw || '');
    // eslint-disable-next-line no-control-regex
    t = t.replace(/[\x00-\x1f\x7f]/g, '');
    return t.split(/\s+/).join(' ').trim();
};
    const addTag = (raw) => {
        const t = cleanTag(raw);
        if (!t) { setTagInput(''); return; }
        const tags = config.ghl_tags || [];
        if (tags.length >= MAX_TAGS) { setTagHint(`Tags are capped at ${MAX_TAGS}`); return; }
        if (t.length > MAX_TAG_LEN) { setTagHint(`Tags are capped at ${MAX_TAG_LEN} characters`); return; }
        if (tags.some(x => x.toLowerCase() === t.toLowerCase())) { setTagHint('That tag already exists'); return; }
        setTagHint(null);
        update('ghl_tags', [...tags, t]);
        setTagInput('');
    };
    const removeTag = (t) => update('ghl_tags', (config.ghl_tags || []).filter(x => x !== t));

    const tagsEditor = (
        <div>
            <input
                value={tagInput}
                onChange={e => { setTagInput(e.target.value); setTagHint(null); }}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
                }}
                onBlur={() => addTag(tagInput)}
                placeholder="Type a tag and press Enter (or comma)"
                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg"
            />
            {tagHint && <p className="mt-1 text-[10px] text-amber-600">{tagHint}</p>}
            <div className="flex flex-wrap gap-1.5 mt-2">
                {(config.ghl_tags || []).map(t => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-stone-100 border border-stone-200 rounded-full text-stone-700">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="text-stone-400 hover:text-red-500 text-sm" title="Remove tag">×</button>
                    </span>
                ))}
            </div>
            <p className="mt-2 text-[10px] text-stone-400">
                Tags are only ever added. Removing a tag here stops it being sent on future submissions —
                it does not remove the tag from contacts already tagged.
            </p>
        </div>
    );

    const mappings = config.ghl_field_mappings || [];
    const setMapping = (formFieldId, encoded) => {
        // encoded: '' | 'standard:email' | 'custom:<ghl_field_id>'
        const rest = mappings.filter(m => m.form_field_id !== formFieldId);
        if (!encoded) return update('ghl_field_mappings', rest);
        const sepIdx = encoded.indexOf(':');
        const kind = encoded.slice(0, sepIdx);
        const target = encoded.slice(sepIdx + 1);
        const ghlField = kind === 'custom' ? (ghlFields || []).find(f => f.id === target) : null;
        update('ghl_field_mappings', [...rest, {
            form_field_id: formFieldId,
            target_kind: kind,
            target,
            ...(ghlField ? { ghl_data_type: ghlField.dataType, ghl_field_name: ghlField.name } : {}),
        }]);
    };

    // ── Section summaries (reflect current config; nothing writes into config) ──
    const nonStructuralFields = (formFields || []).filter(f => !STRUCTURAL_TYPES.includes(f.field_type));
    const mappedCount = nonStructuralFields.filter(f => {
        const fid = f.field_id || f.id;
        return mappings.some(m => m.form_field_id === fid);
    }).length;
    const staleMappings = mappings.filter(m => !(formFields || []).some(f => (f.field_id || f.id) === m.form_field_id));
    const hasProblem = staleMappings.length > 0 || !!ghlError;

    const generalSummary = `${config.name || 'Unnamed'} · ${config.type === 'gohighlevel' ? 'GoHighLevel' : 'Custom API'}`;
    const ghlConnSummary = config.ghl_api_key ? maskKey(config.ghl_api_key) : 'Not configured';
    const mappingSummary = `${mappedCount} of ${nonStructuralFields.length} mapped` + (staleMappings.length ? ` · ${staleMappings.length} stale` : '');
    const tags = config.ghl_tags || [];
    const tagsSummary = tags.length ? `${tags.length} ${tags.length === 1 ? 'tag' : 'tags'}` : 'No tags';
    const contactSummary = config.correlation_source_step_id
        ? `Updates from "${(correlationSources.find(f => f.step_id === config.correlation_source_step_id)?.name) || config.correlation_source_step_id}"`
        : 'Creates new contact';
    const genericConnSummary = [
        (config.method || 'POST').toUpperCase(),
        config.url ? config.url.replace(/^https?:\/\//, '') : 'No endpoint',
        config.auth?.type && config.auth.type !== 'none' ? authLabel(config.auth.type) : null,
    ].filter(Boolean).join(' · ');
    const configSummary = generalSummary + ' · ' + (config.type === 'gohighlevel' ? ghlConnSummary : genericConnSummary);
    const payloadSummary = config.payload_template ? 'Template set' : 'Empty';

    const ghlConnectionFields = (
        <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-stone-600 mb-1">Location ID</label><input value={config.ghl_location_id ?? config.location_id ?? ''} onChange={e => update('ghl_location_id', e.target.value)} placeholder="VeX2kL9mQ3pR7nBx" disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg font-mono disabled:bg-stone-50" /></div>
            <div><label className="block text-xs font-medium text-stone-600 mb-1">Private Integration Token</label><SecretInput testId="ghl-token" value={config.ghl_api_key ?? config.api_key ?? ''} onChange={v => update('ghl_api_key', v)} placeholder="pit-..." disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg font-mono disabled:bg-stone-50" />
                <p className="text-[10px] text-stone-400 mt-1">
                    GHL sub-account → Settings → Private Integrations. Scopes:
                    contacts.write, locations/customFields.readonly
                </p></div>
        </div>
    );

    const genericConnectionFields = (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-stone-600 mb-1">Endpoint URL</label><input data-testid="forwarder-url" value={config.url || ''} onChange={e => update('url', e.target.value)} placeholder="https://api.example.com/webhook" disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg font-mono disabled:bg-stone-50" /></div>
                <div><label className="block text-xs font-medium text-stone-600 mb-1">HTTP Method</label><select value={config.method || 'POST'} onChange={e => update('method', e.target.value)} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white"><option value="POST">POST</option><option value="PUT">PUT</option><option value="GET">GET</option><option value="PATCH">PATCH</option></select></div>
            </div>
            <WebhookDestinationStatus config={config} onStateChange={setConfig} onVerify={verifyDestination} onReveal={revealSecret} />
            <div><label className="block text-xs font-medium text-stone-600 mb-1">Authentication Type</label>
                <select value={config.auth?.type || 'none'} onChange={e => updateAuth('type', e.target.value)} disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white disabled:bg-stone-50">
                    <option value="none">None</option><option value="bearer">Bearer Token</option><option value="api_key">API Key Header</option><option value="basic">Basic Auth</option><option value="custom_headers">Custom Headers</option>
                </select>
            </div>
            {config.auth?.type === 'bearer' && <div><label className="block text-xs font-medium text-stone-600 mb-1">Token</label><SecretInput testId="auth-token" value={config.auth?.token || ''} onChange={v => updateAuth('token', v)} disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg font-mono disabled:bg-stone-50" /></div>}
            {config.auth?.type === 'api_key' && <div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-medium text-stone-600 mb-1">Header Name</label><input value={config.auth?.header_name || 'X-API-Key'} onChange={e => updateAuth('header_name', e.target.value)} disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg font-mono disabled:bg-stone-50" /></div><div><label className="block text-xs font-medium text-stone-600 mb-1">Value</label><SecretInput testId="auth-header-value" value={config.auth?.header_value || ''} onChange={v => updateAuth('header_value', v)} disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg font-mono disabled:bg-stone-50" /></div></div>}
            {config.auth?.type === 'basic' && <div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-medium text-stone-600 mb-1">Username</label><input value={config.auth?.username || ''} onChange={e => updateAuth('username', e.target.value)} disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg disabled:bg-stone-50" /></div><div><label className="block text-xs font-medium text-stone-600 mb-1">Password</label><SecretInput testId="auth-password" value={config.auth?.password || ''} onChange={v => updateAuth('password', v)} disabled={!canEditDestination} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg disabled:bg-stone-50" /></div></div>}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden grid-rows-[auto_minmax(0,1fr)_auto]">
                <DialogHeader className="pb-4 border-b border-stone-100 -mx-6 px-6"><DialogTitle>{forwarder ? 'Edit' : 'Add'} Forwarder</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2 -mx-6 px-6 overflow-y-auto min-h-0">
                    {/* ── Configuration (name, destination type, connection) ── */}
                    <Section title="Configuration" summary={configSummary} defaultOpen>
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Forwarder Name</label>
                            <input value={config.name || ''} onChange={e => update('name', e.target.value)} placeholder="e.g. Send to GHL" className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div onClick={() => canEditDestination && update('type', 'gohighlevel')} className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${config.type === 'gohighlevel' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'}`}>
                                <div className="text-sm font-semibold text-stone-900">GoHighLevel</div>
                                <div className="text-xs text-stone-500">Native integration</div>
                            </div>
                            <div onClick={() => canEditDestination && update('type', 'generic_api')} className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${config.type === 'generic_api' ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'}`}>
                                <div className="text-sm font-semibold text-stone-900">Custom API</div>
                                <div className="text-xs text-stone-500">Send to any endpoint</div>
                            </div>
                        </div>

                        {!canEditDestination && (
                            <p data-testid="destination-admin-only" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                                Only an organization admin can change where this forwarder delivers or its credentials.
                            </p>
                        )}
                        {config.type === 'gohighlevel' && ghlConnectionFields}
                        {config.type === 'generic_api' && genericConnectionFields}
                    </Section>

                    {config.type === 'gohighlevel' && (
                        <>

                            {/* ── Field Mapping (auto-expands over a stale row or GHL error) ── */}
                            <Section title="Field Mapping" summary={mappingSummary} defaultOpen={mappedCount > 0 || staleMappings.length > 0} forceOpen={hasProblem}>
                                <div className="border border-stone-200 rounded-lg p-3 space-y-3">
                                    <div className="flex items-center justify-end">
                                        <button
                                            type="button"
                                            onClick={loadGhlFields}
                                            disabled={loadingFields}
                                            hidden={!canEditDestination}
                                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-stone-200 rounded-lg hover:border-stone-400 text-stone-600 disabled:opacity-50"
                                        >
                                            {loadingFields ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                            Refresh GHL fields
                                        </button>
                                    </div>
                                    {(ghlFields || ghlError) && (
                                        <div className={`flex items-center gap-1.5 text-xs ${ghlError ? 'text-red-600' : 'text-emerald-600'}`}>
                                            {ghlError ? <XCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                                            {ghlError || `Connected — ${ghlFields.length} custom fields loaded`}
                                        </div>
                                    )}
                                    <div className="space-y-1">
                                        <div className="grid grid-cols-2 gap-2 text-[10px] font-medium text-stone-400 uppercase tracking-wide pb-1 border-b border-stone-100">
                                            <div>Form Field</div>
                                            <div>GHL Target</div>
                                        </div>
                                        {nonStructuralFields.map(f => {
                                            const fid = f.field_id || f.id;
                                            const mapping = mappings.find(m => m.form_field_id === fid);
                                            const encoded = mapping ? `${mapping.target_kind}:${mapping.target}` : '';
                                            return (
                                                <div key={fid} className="grid grid-cols-2 gap-2 items-center py-1">
                                                    <div className="text-xs text-stone-700 truncate">
                                                        {f.label || f.question || 'Untitled'}
                                                        <span className="text-stone-400 ml-1">({f.field_type})</span>
                                                        {f.hidden && (
                                                            <span className="ml-1 px-1 rounded bg-indigo-50 text-indigo-600 text-[9px]">
                                                                hidden
                                                            </span>
                                                        )}
                                                    </div>
                                                    <select
                                                        value={encoded}
                                                        onChange={e => setMapping(fid, e.target.value)}
                                                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-lg bg-white"
                                                    >
                                                        <option value="">Not mapped</option>
                                                        <optgroup label="Standard fields">
                                                            {GHL_STANDARD_FIELDS.map(sf => (
                                                                <option key={sf.value} value={`standard:${sf.value}`}>{sf.label}</option>
                                                            ))}
                                                        </optgroup>
                                                        <optgroup label={ghlFields ? `Custom fields (${ghlFields.length})` : 'Custom fields — load first'} disabled={!ghlFields}>
                                                            {ghlFields
                                                                ? ghlFields.map(cf => (
                                                                    <option key={cf.id} value={`custom:${cf.id}`}>{cf.name} · {cf.dataType}</option>
                                                                ))
                                                                : <option value="" disabled>Load GHL fields to enable</option>
                                                            }
                                                        </optgroup>
                                                    </select>
                                                </div>
                                            );
                                        })}
                                        {staleMappings.map(m => (
                                            <div key={`stale-${m.form_field_id}`} className="grid grid-cols-2 gap-2 items-center py-1 bg-red-50 rounded px-1">
                                                <div className="text-xs text-red-600 flex items-center gap-1">
                                                    <XCircle className="w-3 h-3 flex-shrink-0" />
                                                    <span className="truncate">Deleted field</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-xs text-stone-500 flex-1 truncate">
                                                        {m.target_kind === 'custom' ? `${m.ghl_field_name || m.target}${m.ghl_data_type ? ' · ' + m.ghl_data_type : ''}` : m.target}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setMapping(m.form_field_id, '')}
                                                        className="text-red-500 hover:text-red-700 text-sm flex-shrink-0"
                                                        title="Remove mapping"
                                                    >✕</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Section>

                            {/* ── Tags (add-only; GHL forwarders only) ── */}
                            <Section title="Tags" summary={tagsSummary} defaultOpen={tags.length > 0}>
                                {tagsEditor}
                            </Section>

                            {/* ── Contact linking (Send UTM + correlation) ── */}
                            <Section title="Contact linking" summary={contactSummary} defaultOpen={!!config.correlation_source_step_id}>
                                <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer"><input type="checkbox" checked={!!config.send_utm} onChange={e => update('send_utm', e.target.checked)} className="rounded border-stone-300" /> Send UTM Parameters</label>
                                <div className="border-t pt-3">
                                    <label className="block text-sm font-medium text-stone-700 mb-1">
                                        Update existing contact from
                                    </label>
                                    <p className="text-[11px] text-stone-400 mb-2">
                                        Select a prior forwarder node to update the same GHL contact it created.
                                        Leave empty to create a new contact (upsert by email).
                                    </p>
                                    <select
                                        value={config.correlation_source_step_id || ''}
                                        onChange={e => update('correlation_source_step_id', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white"
                                    >
                                        <option value="">— Create new contact (default) —</option>
                                        {correlationSources.map(f => (
                                            <option key={f.step_id} value={f.step_id}>
                                                {f.name || f.step_id}
                                            </option>
                                        ))}
                                    </select>
                                    {config.correlation_source_step_id && (
                                        <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                                            This forwarder will update the GHL contact created by
                                            <strong> {correlationSources.find(f => f.step_id === config.correlation_source_step_id)?.name || config.correlation_source_step_id}</strong>.
                                            If that forwarder fails, this forwarder is skipped.
                                        </p>
                                    )}
                                </div>
                            </Section>
                        </>
                    )}

                    {config.type === 'generic_api' && (
                        <>
                            {/* ── Payload (template + insert chips + preview) ── */}
                            <Section title="Payload" summary={payloadSummary} defaultOpen={!!config.payload_template}>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-xs font-medium text-stone-600">Payload Template (Jinja2)</label>
                                        <div className="flex gap-1.5">
                                            <button type="button" onClick={() => {
                                                const result = formatJinjaJson(config.payload_template || '');
                                                if (result.ok) update('payload_template', result.text);
                                            }} className="px-2 py-0.5 text-xs border border-stone-200 rounded hover:border-stone-400 text-stone-600">Format</button>
                                            <button type="button" onClick={async () => {
                                                setTesting(true); setPreview(null);
                                                try {
                                                    const res = await axios.post(`${API}/forms/forwarders/test`,
                                                        { config, sample_data: null, dry_run: true, form_id: formId },
                                                        { headers: getAuthHeader() });
                                                    setPreview(res.data);
                                                    if (!res.data.success) setTestResult({ success: false, message: res.data.error });
                                                } catch (err) { setTestResult({ success: false, message: err.response?.data?.detail || 'Preview failed' }); }
                                                finally { setTesting(false); }
                                            }} hidden={!canEditDestination} className="px-2 py-0.5 text-xs border border-stone-200 rounded hover:border-stone-400 text-stone-600">Preview</button>
                                        </div>
                                    </div>
                                    <CodeTextarea
                                        ref={textareaRef}
                                        value={config.payload_template || ''}
                                        onChange={v => update('payload_template', v)}
                                        placeholder={'{\n  "email": "{{ answers.<field-uuid> | tojson }}",\n  "services": "{{ answers.<field-uuid> | tojson }}"\n}'}
                                        className="w-full min-h-[120px] px-3 py-2 text-xs font-mono border border-stone-200 rounded-lg bg-stone-900 text-stone-100"
                                    />
                                    <p className="text-[10px] text-stone-400 mt-1">
                                        Use `| tojson` for values that may contain quotes; multi-select answers render as JSON arrays.
                                        Tab=indent · Shift+Tab=outdent · Esc then Tab=leave
                                    </p>
                                </div>
                                <div>
                                    <div className="text-xs text-stone-400 mb-1">Click to insert (at cursor):</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {varHints.map(v => <button key={v} onClick={() => {
                                            const ta = textareaRef.current;
                                            if (ta && typeof ta.setRangeText === 'function') {
                                                ta.setRangeText(v, ta.selectionStart, ta.selectionEnd, 'end');
                                                update('payload_template', ta.value);
                                            } else {
                                                update('payload_template', (config.payload_template || '') + v);
                                            }
                                        }} className="px-2 py-1 text-xs font-mono border border-stone-200 rounded hover:border-stone-400 text-stone-600">{v}</button>)}
                                    </div>
                                </div>
                                {preview && preview.success && (
                                    <div className="mt-2 p-3 bg-stone-50 border border-stone-200 rounded-lg">
                                        <div className="text-xs text-stone-500 mb-1">Preview (dry run — nothing is sent):</div>
                                        <div className="text-xs font-mono text-stone-600 mb-1">POST {preview.url || '—'}</div>
                                        <pre className="text-xs font-mono text-stone-700 overflow-x-auto">{JSON.stringify(preview.rendered_payload, null, 2)}</pre>
                                    </div>
                                )}
                            </Section>
                        </>
                    )}

                    {testResult && <div className={`flex items-center gap-2 text-xs p-2 rounded-lg ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{testResult.success ? `Test successful — ${testResult.message}` : `Test failed — ${testResult.message}`}</div>}
                </div>
                <DialogFooter className="pt-4 border-t border-stone-100 -mx-6 px-6">
                    {canEditDestination && <Button variant="outline" onClick={test} disabled={testing}>{testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Test Connection</Button>}
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => { onSave(config); onClose(); }}>Save Forwarder</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
